import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const Admin = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [team, setTeam] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  // Create team state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [teamName, setTeamName] = useState("");
  const [teamDomain, setTeamDomain] = useState("");

  // Invite state
  const [inviteEmail, setInviteEmail] = useState("");

  // SAML state
  const [samlModalOpen, setSamlModalOpen] = useState(false);
  const [samlConfig, setSamlConfig] = useState(null);
  const [samlForm, setSamlForm] = useState({
    idp_entity_id: "",
    idp_sso_url: "",
    idp_certificate: "",
    idp_metadata_url: "",
  });

  // Team analytics
  const [teamAnalytics, setTeamAnalytics] = useState(null);

  const fetchUser = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch {
      navigate("/");
    }
  }, [navigate]);

  const fetchTeam = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/team`);
      setTeam(res.data.team);
    } catch (e) {
      console.error("Team fetch error:", e);
    }
  }, []);

  const fetchUsage = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/usage`);
      setUsage(res.data);
    } catch {
      // No team yet
    }
  }, []);

  const fetchSamlConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/saml/config`);
      setSamlConfig(res.data);
    } catch {
      setSamlConfig({ configured: false });
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      await fetchUser();
      await fetchTeam();
      await fetchUsage();
      await fetchSamlConfig();
      // Fetch team analytics
      try {
        const res = await axios.get(`${API}/analytics/team`);
        setTeamAnalytics(res.data);
      } catch (e) { /* Team analytics may not be available */ }
      setLoading(false);
    };
    init();
  }, [fetchUser, fetchTeam, fetchUsage, fetchSamlConfig]);

  const handleCreateTeam = async () => {
    if (!teamName.trim()) return;
    try {
      await axios.post(`${API}/admin/team`, { name: teamName, domain: teamDomain || null });
      toast.success("Team created!");
      setCreateModalOpen(false);
      setTeamName("");
      fetchTeam();
      fetchUsage();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create team");
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    try {
      const res = await axios.post(`${API}/admin/invite`, { email: inviteEmail });
      toast.success(res.data.message);
      setInviteEmail("");
      fetchTeam();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to invite");
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm("Remove this member from the team?")) return;
    try {
      await axios.delete(`${API}/admin/member/${memberId}`);
      toast.success("Member removed");
      fetchTeam();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    }
  };

  const handleSamlSave = async () => {
    if (!samlForm.idp_entity_id || !samlForm.idp_sso_url || !samlForm.idp_certificate) {
      toast.error("Entity ID, SSO URL, and Certificate are required");
      return;
    }
    try {
      const res = await axios.post(`${API}/admin/saml/configure`, samlForm);
      toast.success("SAML SSO configured!");
      setSamlModalOpen(false);
      fetchSamlConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to configure SAML");
    }
  };

  const handleSamlDelete = async () => {
    if (!window.confirm("Disable SAML SSO? Members will need to use Google Sign-In.")) return;
    try {
      await axios.delete(`${API}/admin/saml/config`);
      toast.success("SAML SSO disabled");
      fetchSamlConfig();
    } catch (e) {
      toast.error("Failed to disable SAML");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0600] flex items-center justify-center">
        <div className="spinner-ember" />
      </div>
    );
  }

  const isOwner = team && team.owner_id === user?.user_id;

  return (
    <div className="min-h-screen bg-[#0A0600]" data-testid="admin-page">
      {/* Header */}
      <header className="h-[56px] border-b border-[rgba(232,160,48,0.1)] bg-[#1C1004] flex items-center justify-between px-6">
        <div className="flex items-center gap-4">
          <Link to="/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="w-[9px] h-[9px] bg-[#E8A030] rotate-45" />
            <span className="font-serif font-black text-lg text-[#FDF8EE]">Ryan</span>
          </Link>
          <span className="font-mono text-[9px] text-[#4A3A1A] uppercase tracking-[1px]">/ Admin</span>
        </div>
        <Link
          to="/dashboard"
          className="font-mono text-[9px] text-[#8C7A5A] hover:text-[#E8A030] uppercase tracking-[1px] transition-colors"
        >
          Back to Dashboard
        </Link>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {!team ? (
          /* No team yet */
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-[rgba(232,160,48,0.08)] border border-[rgba(232,160,48,0.15)] flex items-center justify-center mx-auto mb-6">
              <span className="text-2xl text-[#E8A030] font-serif font-bold">R</span>
            </div>
            <h1 className="font-serif text-2xl font-bold text-[#FDF8EE] mb-2">Enterprise Team</h1>
            <p className="text-sm text-[#8C7A5A] max-w-md mx-auto mb-8">
              Create a team to manage members, share volumes, and configure enterprise SSO.
            </p>
            <button
              onClick={() => setCreateModalOpen(true)}
              className="btn-primary"
              data-testid="create-team-btn"
            >
              Create Team
            </button>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Team Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="font-serif text-2xl font-bold text-[#FDF8EE]" data-testid="team-name">{team.name}</h1>
                <p className="font-mono text-[10px] text-[#4A3A1A] uppercase tracking-[1px] mt-1">
                  {team.member_count} member{team.member_count !== 1 ? "s" : ""}
                  {team.domain && ` · ${team.domain}`}
                </p>
              </div>
              {isOwner && (
                <button
                  onClick={() => setSamlModalOpen(true)}
                  className="font-mono text-[9px] text-[#E8A030] hover:bg-[rgba(232,160,48,0.1)] px-3 py-2 border border-[rgba(232,160,48,0.2)] uppercase tracking-[1px] transition-colors"
                  data-testid="configure-sso-btn"
                >
                  {samlConfig?.configured ? "Manage SSO" : "Configure SSO"}
                </button>
              )}
            </div>

            {/* Usage Stats */}
            {usage && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: "Members", value: usage.total_members },
                  { label: "Bookmarks", value: usage.total_bookmarks },
                  { label: "Volumes", value: usage.total_collections },
                  { label: "Ask Ryan Queries", value: usage.total_ask_ryan_queries },
                ].map((stat) => (
                  <div key={stat.label} className="card-coke p-4 text-center" data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}>
                    <div className="text-2xl font-bold text-[#E8A030] font-serif">{stat.value}</div>
                    <div className="font-mono text-[8px] text-[#4A3A1A] uppercase tracking-[1px] mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Team Growth Chart */}
            {teamAnalytics?.daily_growth && (
              <div className="card-coke p-6">
                <h2 className="font-serif text-lg font-bold text-[#FDF8EE] mb-1">Team Growth — Last 30 Days</h2>
                <p className="font-mono text-[9px] text-[#4A3A1A] uppercase tracking-[1px] mb-4">
                  {teamAnalytics.daily_growth.reduce((s, d) => s + d.count, 0)} entries added · {teamAnalytics.shared_volumes} shared volumes
                </p>
                <div className="flex items-end gap-[3px] h-24" data-testid="team-growth-chart">
                  {teamAnalytics.daily_growth.map((day, i) => {
                    const maxG = Math.max(...teamAnalytics.daily_growth.map(d => d.count), 1);
                    const height = day.count > 0 ? Math.max((day.count / maxG) * 100, 4) : 2;
                    return (
                      <div key={i} className="flex-1 group relative flex flex-col items-center justify-end">
                        <div
                          className={`w-full transition-all ${day.count > 0 ? 'bg-[#A08CC8] group-hover:bg-[#B8A0E0]' : 'bg-[rgba(160,140,200,0.08)]'}`}
                          style={{ height: `${height}%`, minHeight: day.count > 0 ? '4px' : '2px' }}
                          title={`${day.date}: ${day.count}`}
                        />
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between mt-2">
                  <span className="font-mono text-[8px] text-[#4A3A1A]">{teamAnalytics.daily_growth[0]?.date}</span>
                  <span className="font-mono text-[8px] text-[#4A3A1A]">{teamAnalytics.daily_growth[teamAnalytics.daily_growth.length - 1]?.date}</span>
                </div>
              </div>
            )}

            {/* SSO Status */}
            {samlConfig?.configured && (
              <div className="card-coke p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-[#7AB8A0]">&#9679;</span>
                  <div>
                    <p className="text-sm text-[#FDF8EE] font-medium">SAML SSO Active</p>
                    <p className="font-mono text-[9px] text-[#4A3A1A]">
                      IdP: {samlConfig.idp_entity_id}
                    </p>
                  </div>
                </div>
                <div className="font-mono text-[8px] text-[#4A3A1A]">
                  SSO Login: /auth/saml/login/{team.team_id}
                </div>
              </div>
            )}

            {/* Members */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif text-lg font-bold text-[#FDF8EE]">Team Members</h2>
                {isOwner && (
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="email@company.com"
                      className="input-dark text-sm w-60"
                      data-testid="invite-email-input"
                    />
                    <button
                      onClick={handleInvite}
                      disabled={!inviteEmail.trim()}
                      className="btn-primary text-sm disabled:opacity-30"
                      data-testid="invite-btn"
                    >
                      Invite
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                {team.members?.map((m) => (
                  <div
                    key={m.user_id}
                    className="card-coke p-4 flex items-center justify-between"
                    data-testid={`member-${m.user_id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-9 h-9 bg-[rgba(232,160,48,0.08)] border border-[rgba(232,160,48,0.12)] flex items-center justify-center">
                        <span className="text-[#E8A030] text-sm font-serif font-bold">
                          {(m.email || "?").charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm text-[#FDF8EE]">{m.email}</p>
                        <div className="flex items-center gap-3 mt-0.5">
                          <span className={`font-mono text-[8px] uppercase tracking-[1px] ${m.role === "admin" ? "text-[#E8A030]" : "text-[#4A3A1A]"}`}>
                            {m.role}
                          </span>
                          <span className="font-mono text-[8px] text-[#4A3A1A]">
                            {m.bookmarks} bookmarks · {m.collections} collections
                          </span>
                        </div>
                      </div>
                    </div>
                    {isOwner && m.user_id !== user?.user_id && (
                      <button
                        onClick={() => handleRemoveMember(m.user_id)}
                        className="font-mono text-[9px] text-red-400 hover:underline"
                        data-testid={`remove-member-${m.user_id}`}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Create Team Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="modal-dark sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-[#FDF8EE]">Create Team</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <label className="font-mono text-[9px] text-[#4A3A1A] uppercase tracking-[1px] block mb-1">Team Name</label>
              <input
                type="text"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                placeholder="Acme Corp"
                className="input-dark w-full"
                data-testid="team-name-input"
              />
            </div>
            <div>
              <label className="font-mono text-[9px] text-[#4A3A1A] uppercase tracking-[1px] block mb-1">Domain (optional)</label>
              <input
                type="text"
                value={teamDomain}
                onChange={(e) => setTeamDomain(e.target.value)}
                placeholder="acme.com"
                className="input-dark w-full"
              />
            </div>
            <button onClick={handleCreateTeam} disabled={!teamName.trim()} className="btn-primary w-full disabled:opacity-30" data-testid="create-team-submit">
              Create Team
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* SAML Config Modal */}
      <Dialog open={samlModalOpen} onOpenChange={setSamlModalOpen}>
        <DialogContent className="modal-dark sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-[#FDF8EE]">SAML SSO Configuration</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-[#8C7A5A] leading-relaxed">
              Connect your identity provider (Okta, Azure AD, OneLogin, etc.) for enterprise single sign-on. Your team members will be able to log in via SSO.
            </p>

            {samlConfig?.configured && (
              <div className="p-3 bg-[rgba(122,184,160,0.06)] border border-[rgba(122,184,160,0.15)]">
                <p className="text-sm text-[#7AB8A0] font-medium">SSO is currently active</p>
                <div className="mt-2 space-y-1 font-mono text-[9px] text-[#4A3A1A]">
                  <p>SP Entity ID: {samlConfig.sp_entity_id}</p>
                  <p>ACS URL: {samlConfig.sp_acs_url}</p>
                  <p>SSO Login: /api/auth/saml/login/{team?.team_id}</p>
                  <p>Metadata: /api/admin/saml/metadata/{team?.team_id}</p>
                </div>
              </div>
            )}

            <div>
              <label className="font-mono text-[9px] text-[#4A3A1A] uppercase tracking-[1px] block mb-1">IdP Entity ID</label>
              <input
                type="text"
                value={samlForm.idp_entity_id}
                onChange={(e) => setSamlForm(p => ({ ...p, idp_entity_id: e.target.value }))}
                placeholder="https://idp.example.com/entity"
                className="input-dark w-full"
                data-testid="saml-entity-id-input"
              />
            </div>
            <div>
              <label className="font-mono text-[9px] text-[#4A3A1A] uppercase tracking-[1px] block mb-1">IdP SSO URL</label>
              <input
                type="text"
                value={samlForm.idp_sso_url}
                onChange={(e) => setSamlForm(p => ({ ...p, idp_sso_url: e.target.value }))}
                placeholder="https://idp.example.com/sso/saml"
                className="input-dark w-full"
                data-testid="saml-sso-url-input"
              />
            </div>
            <div>
              <label className="font-mono text-[9px] text-[#4A3A1A] uppercase tracking-[1px] block mb-1">IdP Certificate (X.509)</label>
              <textarea
                value={samlForm.idp_certificate}
                onChange={(e) => setSamlForm(p => ({ ...p, idp_certificate: e.target.value }))}
                placeholder="MIICwDCCAai..."
                className="input-dark w-full h-24 resize-none"
                data-testid="saml-cert-input"
              />
            </div>
            <div>
              <label className="font-mono text-[9px] text-[#4A3A1A] uppercase tracking-[1px] block mb-1">IdP Metadata URL (optional)</label>
              <input
                type="text"
                value={samlForm.idp_metadata_url}
                onChange={(e) => setSamlForm(p => ({ ...p, idp_metadata_url: e.target.value }))}
                placeholder="https://idp.example.com/metadata"
                className="input-dark w-full"
              />
            </div>

            <div className="flex gap-2">
              <button onClick={handleSamlSave} className="btn-primary flex-1" data-testid="saml-save-btn">
                {samlConfig?.configured ? "Update SSO" : "Enable SSO"}
              </button>
              {samlConfig?.configured && (
                <button onClick={handleSamlDelete} className="px-4 py-2 border border-[rgba(255,0,0,0.2)] text-red-400 font-mono text-[9px] uppercase tracking-[1px] hover:bg-[rgba(255,0,0,0.1)] transition-colors" data-testid="saml-disable-btn">
                  Disable
                </button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
